import React, { useState } from 'react';
import { useMe } from '../useMe';
import { useQueryFeatures, useToggleFeature } from './featureActions';
import {
  getFeatureLabel,
  getParentFeature,
  isParentFeature,
  ORGANISATION_FEATURE,
  organisationFeatures,
} from './helper';
import { groupBy } from 'lodash';
import SalvPage from '../../common/SalvPage';
import { SalvCard, SalvFieldHeader, SalvLabel, SalvSwitch } from '@salvapp/ui-components';
import './Features.scss';
import { NoMatch } from '../../route/NoMatch';
import { ExpansionOptions } from '@salvapp/ui-components/build/SalvCard/SalvCard.types';

export const Features = () => {
  const [showInDevelopment, setShowInDevelopment] = useState(false);
  const { currentOrganisation } = useMe();

  const { data: enabledFeatures, isLoading } = useQueryFeatures(currentOrganisation?.id);

  const isFeatureActive = (feature: ORGANISATION_FEATURE): boolean => {
    return !!enabledFeatures?.map((element) => element.feature).includes(feature);
  };

  const { mutate: toggleFeature } = useToggleFeature(currentOrganisation?.id, currentOrganisation?.id);

  const featureMap = Object.entries(groupBy(organisationFeatures, (feat) => getParentFeature(feat.key)[0]))
    .concat([[ORGANISATION_FEATURE.RISK, []]])
    .sort((a, b) => a[0].localeCompare(b[0]));

  const initialExpansions = featureMap.map((_) => false);
  const [expansions, setExpansions] = useState(initialExpansions);

  const getExpansionOptions = (index: number, isParentActive: boolean): ExpansionOptions => {
    return {
      isDisabled: !isParentActive,
      isExpanded: expansions[index],
      onToggle: () => {
        const newExpansions = [...initialExpansions];
        newExpansions[index] = !expansions[index];
        setExpansions(newExpansions);
      },
    };
  };

  if (!currentOrganisation) {
    return <NoMatch />;
  }

  return (
    <SalvPage
      title={'Features'}
      isLoading={isLoading}
      actions={
        <SalvSwitch
          label={'Show in development features'}
          selected={showInDevelopment}
          onChange={() => setShowInDevelopment(!showInDevelopment)}
        />
      }
    >
      <div className='features-page-column'>
        {featureMap.map(([parent, features], index) => {
          const parentFeat = organisationFeatures.find((feature) => feature.key === parent);
          const isParentActive = parentFeat ? isFeatureActive(parentFeat.key) : true;
          const parentName = parentFeat ? getFeatureLabel(parentFeat.key) : 'Generic features';
          return (
            <div key={index} aria-label={parentName + ' card'}>
              <SalvCard expansionOptions={features.length > 0 ? getExpansionOptions(index, isParentActive) : undefined}>
                <SalvCard.Header
                  actions={
                    parentFeat && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <SalvSwitch
                          ariaLabel={getFeatureLabel(parentFeat.key)}
                          selected={isFeatureActive(parentFeat.key)}
                          onChange={(isToggled) => {
                            const isParentActive = isFeatureActive(parentFeat.key);
                            // disable all children under parent
                            if (isParentActive) {
                              features
                                .filter((feat) => isFeatureActive(feat.key))
                                .forEach((feature) =>
                                  toggleFeature({ feature: feature.key, isFeatureActive: isParentActive })
                                );
                            }

                            toggleFeature({ feature: parentFeat.key, isFeatureActive: isParentActive });

                            initialExpansions[index] = isToggled;
                            setExpansions(initialExpansions);
                          }}
                        />
                      </div>
                    )
                  }
                >
                  <div className={'parent-feature'}>
                    <SalvFieldHeader
                      label={parentName}
                      key={parentFeat ? parentFeat.key : 'Generic features'}
                      description={parentFeat ? parentFeat.description : 'Features not related to any specific product'}
                    />
                  </div>
                </SalvCard.Header>
                {features.length > 0 && (
                  <SalvCard.Body>
                    {isParentActive &&
                      features
                        .filter((feat) => !isParentFeature(feat.key))
                        .filter((feat) => showInDevelopment || !feat.inDevelopment)
                        .map((feature) => (
                          <div key={feature.key} className='child-feature'>
                            <div>
                              <div className='text-m d-flex'>
                                {getFeatureLabel(feature.key)}
                                {feature.inDevelopment && (
                                  <SalvLabel variant='warning' key={feature.key + '_flag'} className='m-l-4'>
                                    In Development
                                  </SalvLabel>
                                )}
                              </div>
                              <div className={'text-muted font-size-sm'}>{feature.description}</div>
                            </div>
                            <SalvSwitch
                              ariaLabel={getFeatureLabel(feature.key)}
                              selected={isFeatureActive(feature.key)}
                              className={'ms-auto'}
                              onChange={() =>
                                toggleFeature({
                                  feature: feature.key,
                                  isFeatureActive: isFeatureActive(feature.key),
                                })
                              }
                            />
                          </div>
                        ))}
                  </SalvCard.Body>
                )}
              </SalvCard>
            </div>
          );
        })}
      </div>
    </SalvPage>
  );
};

export default Features;
